import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { 
  Bot, Send, MessageSquare, TrendingUp, Briefcase, FileText, 
  Sparkles, Loader2, Trash2, RefreshCw, Brain, BarChart3,
  ArrowUpRight, ArrowDownRight, Target
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ICONS = {
  cocoa: "🍫", coffee: "☕", sugar: "🍬", cotton: "🧵", wheat: "🌾", 
  maize: "🌽", soybean: "🫘", palm_oil: "🌴", gold: "🥇", crude_oil: "🛢️",
  copper: "🔶", natural_gas: "🔥", silver: "🥈", zinc: "⚪"
};

export default function AIAssistantPage() {
  const [activeTab, setActiveTab] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [marketIntelligence, setMarketIntelligence] = useState(null);
  const [portfolioAdvice, setPortfolioAdvice] = useState(null);
  const [predictions, setPredictions] = useState({});
  const [research, setResearch] = useState(null);
  const [selectedCommodity, setSelectedCommodity] = useState("gold");
  const [commodities, setCommodities] = useState([]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    fetchCommodities();
  }, []);

  const fetchCommodities = async () => {
    try {
      const res = await api.get("/price-alerts/commodities");
      setCommodities(res.data.commodities || []);
    } catch (err) {
      console.error("Error fetching commodities:", err);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const res = await api.post("/ai/chat", {
        message: userMessage,
        conversation_id: conversationId
      });

      setConversationId(res.data.conversation_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (err) {
      toast.error("Failed to get response");
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I apologize, but I encountered an error. Please try again." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewConversation = () => {
    setMessages([]);
    setConversationId(null);
    toast.success("Started new conversation");
  };

  const fetchMarketIntelligence = async () => {
    setLoading(true);
    try {
      const res = await api.get("/ai/market-intelligence");
      setMarketIntelligence(res.data);
      toast.success("Market intelligence generated!");
    } catch (err) {
      toast.error("Failed to generate market intelligence");
    } finally {
      setLoading(false);
    }
  };

  const fetchPortfolioAdvice = async () => {
    setLoading(true);
    try {
      const res = await api.get("/ai/portfolio-advice");
      setPortfolioAdvice(res.data);
      toast.success("Portfolio advice generated!");
    } catch (err) {
      toast.error("Failed to generate portfolio advice");
    } finally {
      setLoading(false);
    }
  };

  const fetchPrediction = async (commodityId) => {
    setLoading(true);
    try {
      const res = await api.post(`/ai/predict/${commodityId}`);
      setPredictions(prev => ({ ...prev, [commodityId]: res.data }));
      toast.success(`Prediction generated for ${commodityId}!`);
    } catch (err) {
      toast.error("Failed to generate prediction");
    } finally {
      setLoading(false);
    }
  };

  const fetchResearch = async (commodityId) => {
    setLoading(true);
    try {
      const res = await api.get(`/ai/research/${commodityId}`);
      setResearch(res.data);
      toast.success("Research report generated!");
    } catch (err) {
      toast.error("Failed to generate research");
    } finally {
      setLoading(false);
    }
  };

  const currentPrediction = predictions[selectedCommodity];

  return (
    <div data-testid="ai-assistant-page" className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#00ff41] to-emerald-600 flex items-center justify-center">
            <Bot className="w-6 h-6 text-black" />
          </div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
              UWATU AI
            </h1>
            <p className="text-xs text-zinc-500 font-mono">
              Powered by SambaNova • Meta-Llama-3.3-70B
            </p>
          </div>
        </div>
        
        <Badge className="bg-[#00ff41]/20 text-[#00ff41] rounded-none font-mono text-xs">
          <Sparkles className="w-3 h-3 mr-1" /> AI-Powered
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-none p-1 flex-wrap">
          <TabsTrigger value="chat" className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs">
            <MessageSquare className="w-3.5 h-3.5 mr-2" /> Chat
          </TabsTrigger>
          <TabsTrigger value="intelligence" className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs">
            <Brain className="w-3.5 h-3.5 mr-2" /> Market Intel
          </TabsTrigger>
          <TabsTrigger value="portfolio" className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs">
            <Briefcase className="w-3.5 h-3.5 mr-2" /> Portfolio
          </TabsTrigger>
          <TabsTrigger value="predictions" className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs">
            <Target className="w-3.5 h-3.5 mr-2" /> Predictions
          </TabsTrigger>
          <TabsTrigger value="research" className="rounded-none data-[state=active]:bg-zinc-800 data-[state=active]:text-[#00ff41] font-mono text-xs">
            <FileText className="w-3.5 h-3.5 mr-2" /> Research
          </TabsTrigger>
        </TabsList>

        {/* Chat Tab */}
        <TabsContent value="chat">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#00ff41]" />
                AI Assistant
              </CardTitle>
              <Button size="sm" variant="outline" onClick={handleNewConversation} className="rounded-none border-zinc-700 font-mono text-xs">
                <RefreshCw className="w-3 h-3 mr-1" /> New Chat
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[400px] p-4">
                {messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center">
                    <Bot className="w-16 h-16 text-zinc-700 mb-4" />
                    <p className="text-zinc-400 font-mono text-sm mb-2">How can I help you today?</p>
                    <div className="flex flex-wrap gap-2 justify-center mt-4">
                      {["How is my portfolio doing?", "What's the outlook for Gold?", "Best commodities to buy now?"].map((q, i) => (
                        <Button
                          key={i}
                          size="sm"
                          variant="outline"
                          className="rounded-none border-zinc-700 font-mono text-xs"
                          onClick={() => {
                            setInputMessage(q);
                          }}
                        >
                          {q}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[80%] p-3 rounded-none ${
                            msg.role === "user"
                              ? "bg-[#00ff41]/20 text-zinc-100"
                              : "bg-zinc-900 text-zinc-300"
                          }`}
                        >
                          {msg.role === "assistant" && (
                            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-zinc-800">
                              <Bot className="w-4 h-4 text-[#00ff41]" />
                              <span className="text-xs text-[#00ff41] font-mono">UWATU AI</span>
                            </div>
                          )}
                          <p className="text-sm whitespace-pre-wrap font-mono">{msg.content}</p>
                        </div>
                      </div>
                    ))}
                    {loading && (
                      <div className="flex justify-start">
                        <div className="bg-zinc-900 p-3 rounded-none flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-[#00ff41]" />
                          <span className="text-xs text-zinc-400 font-mono">Thinking...</span>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>
              
              <div className="p-4 border-t border-zinc-800">
                <div className="flex gap-2">
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about your portfolio, market trends, or get trading advice..."
                    className="rounded-none border-zinc-700 bg-zinc-950 text-sm font-mono"
                    disabled={loading}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={loading || !inputMessage.trim()}
                    className="rounded-none bg-[#00ff41] text-black hover:bg-[#00ff41]/80"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Market Intelligence Tab */}
        <TabsContent value="intelligence">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Brain className="w-4 h-4 text-[#00ff41]" />
                Market Intelligence
              </CardTitle>
              <Button size="sm" onClick={fetchMarketIntelligence} disabled={loading} className="rounded-none bg-[#00ff41] text-black hover:bg-[#00ff41]/80 font-mono text-xs">
                {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                Generate Report
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              {marketIntelligence?.report ? (
                <div className="space-y-4">
                  <div className="text-xs text-zinc-500 font-mono">
                    Generated: {new Date(marketIntelligence.generated_at).toLocaleString()}
                  </div>
                  <div className="bg-zinc-900 p-4 rounded-none border border-zinc-800">
                    <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">
                      {marketIntelligence.report}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="h-[300px] flex flex-col items-center justify-center text-center">
                  <Brain className="w-16 h-16 text-zinc-700 mb-4" />
                  <p className="text-zinc-400 font-mono text-sm">Click "Generate Report" to create a market intelligence report</p>
                  <p className="text-zinc-600 text-xs mt-2">AI will analyze current market conditions and provide trading signals</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Portfolio Advice Tab */}
        <TabsContent value="portfolio">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4 flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-[#00ff41]" />
                Portfolio Advisor
              </CardTitle>
              <Button size="sm" onClick={fetchPortfolioAdvice} disabled={loading} className="rounded-none bg-[#00ff41] text-black hover:bg-[#00ff41]/80 font-mono text-xs">
                {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                Get Advice
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              {portfolioAdvice?.advice ? (
                <div className="space-y-4">
                  <div className="text-xs text-zinc-500 font-mono">
                    Portfolio Value: ${portfolioAdvice.portfolio_value?.toLocaleString()} • Generated: {new Date(portfolioAdvice.generated_at).toLocaleString()}
                  </div>
                  <div className="bg-zinc-900 p-4 rounded-none border border-zinc-800">
                    <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">
                      {portfolioAdvice.advice}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="h-[300px] flex flex-col items-center justify-center text-center">
                  <Briefcase className="w-16 h-16 text-zinc-700 mb-4" />
                  <p className="text-zinc-400 font-mono text-sm">Click "Get Advice" for AI portfolio recommendations</p>
                  <p className="text-zinc-600 text-xs mt-2">AI will analyze your positions and suggest optimizations</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Predictions Tab */}
        <TabsContent value="predictions">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Target className="w-4 h-4 text-[#00ff41]" />
                Price Predictions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2 mb-4">
                {commodities.slice(0, 8).map(c => (
                  <Button
                    key={c.id}
                    size="sm"
                    variant={selectedCommodity === c.id ? "default" : "outline"}
                    className={`rounded-none font-mono text-xs ${selectedCommodity === c.id ? 'bg-[#00ff41] text-black' : 'border-zinc-700'}`}
                    onClick={() => setSelectedCommodity(c.id)}
                  >
                    {ICONS[c.id]} {c.name}
                  </Button>
                ))}
              </div>
              
              <Button onClick={() => fetchPrediction(selectedCommodity)} disabled={loading} className="rounded-none bg-zinc-800 hover:bg-zinc-700 font-mono text-xs mb-4">
                {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Target className="w-3 h-3 mr-1" />}
                Generate Prediction for {selectedCommodity.replace("_", " ").toUpperCase()}
              </Button>

              {currentPrediction && !currentPrediction.error ? (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-zinc-900 p-4 rounded-none border border-zinc-800">
                    <h3 className="text-xs text-zinc-500 uppercase font-mono mb-3">Predictions</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-400 font-mono text-sm">Current</span>
                        <span className="text-zinc-100 font-mono font-bold">${currentPrediction.current_price?.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-400 font-mono text-sm">24h Prediction</span>
                        <span className={`font-mono font-bold ${currentPrediction.prediction_24h > currentPrediction.current_price ? 'text-[#00ff41]' : 'text-[#ff3b30]'}`}>
                          ${currentPrediction.prediction_24h?.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-400 font-mono text-sm">7d Prediction</span>
                        <span className={`font-mono font-bold ${currentPrediction.prediction_7d > currentPrediction.current_price ? 'text-[#00ff41]' : 'text-[#ff3b30]'}`}>
                          ${currentPrediction.prediction_7d?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-zinc-900 p-4 rounded-none border border-zinc-800">
                    <h3 className="text-xs text-zinc-500 uppercase font-mono mb-3">Analysis</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Badge className={`rounded-none ${currentPrediction.direction === 'bullish' ? 'bg-[#00ff41]/20 text-[#00ff41]' : currentPrediction.direction === 'bearish' ? 'bg-[#ff3b30]/20 text-[#ff3b30]' : 'bg-zinc-700 text-zinc-300'}`}>
                          {currentPrediction.direction === 'bullish' ? <ArrowUpRight className="w-3 h-3 mr-1" /> : currentPrediction.direction === 'bearish' ? <ArrowDownRight className="w-3 h-3 mr-1" /> : null}
                          {currentPrediction.direction?.toUpperCase()}
                        </Badge>
                        <Badge className="rounded-none bg-zinc-700 text-zinc-300">
                          Confidence: {currentPrediction.confidence?.toUpperCase()}
                        </Badge>
                      </div>
                      <p className="text-sm text-zinc-400 font-mono">{currentPrediction.reasoning}</p>
                      <div className="text-xs text-zinc-500 font-mono">
                        Key Factors: {currentPrediction.key_factors?.join(", ")}
                      </div>
                    </div>
                  </div>
                </div>
              ) : currentPrediction?.error ? (
                <div className="text-center p-8 text-zinc-500">Failed to generate prediction</div>
              ) : (
                <div className="h-[200px] flex flex-col items-center justify-center text-center">
                  <Target className="w-12 h-12 text-zinc-700 mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">Select a commodity and generate prediction</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Research Tab */}
        <TabsContent value="research">
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#00ff41]" />
                Research Reports
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-2 mb-4">
                {commodities.slice(0, 8).map(c => (
                  <Button
                    key={c.id}
                    size="sm"
                    variant={selectedCommodity === c.id ? "default" : "outline"}
                    className={`rounded-none font-mono text-xs ${selectedCommodity === c.id ? 'bg-[#00ff41] text-black' : 'border-zinc-700'}`}
                    onClick={() => setSelectedCommodity(c.id)}
                  >
                    {ICONS[c.id]} {c.name}
                  </Button>
                ))}
              </div>
              
              <Button onClick={() => fetchResearch(selectedCommodity)} disabled={loading} className="rounded-none bg-zinc-800 hover:bg-zinc-700 font-mono text-xs mb-4">
                {loading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FileText className="w-3 h-3 mr-1" />}
                Generate Research for {selectedCommodity.replace("_", " ").toUpperCase()}
              </Button>

              {research?.research && research.commodity_id === selectedCommodity ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-xs text-zinc-500 font-mono">
                    <span>{research.commodity_name} • Current: ${research.current_price?.toLocaleString()}</span>
                    <span>Generated: {new Date(research.generated_at).toLocaleString()}</span>
                  </div>
                  <div className="bg-zinc-900 p-4 rounded-none border border-zinc-800 max-h-[500px] overflow-y-auto">
                    <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-mono leading-relaxed">
                      {research.research}
                    </pre>
                  </div>
                </div>
              ) : (
                <div className="h-[200px] flex flex-col items-center justify-center text-center">
                  <FileText className="w-12 h-12 text-zinc-700 mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">Select a commodity and generate research report</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
